import { Lock } from '@mui/icons-material';
import {
  Divider,
  LinearProgress,
  Link,
  Stack,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material';

import { Creator } from '@/models/creator';
import { getRestrictionText } from '@/utils/creator';
import nFormatter from '@/utils/nFormatter/nFormatter';
import { normalizeTag } from '@/utils/text/text';

export interface CreatorAccountProps {
  account: Creator.PlatformAccount;
  showBorderBottom: boolean;
}

const getPlatformFromUrl = (url?: string): string => {
  if (!url) return 'this platform';
  if (url.includes('youtube.com')) return 'YouTube';
  if (url.includes('tiktok.com')) return 'TikTok';
  if (url.includes('instagram.com')) return 'Instagram';
  return 'this platform';
};

const STATUS_TOOLTIPS: Partial<
  Record<Creator.PlatformAccount['status'], (platform: string) => string>
> = {
  missing: (platform) =>
    `This account has been deleted from ${platform}, or never existed.`,
  pending: (platform) =>
    `This account's information is being retrieved from ${platform}.`,
  outdated: (platform) =>
    `This account has been deleted from ${platform}, or changed name.`
};

const CreatorAccount = ({ account, showBorderBottom }: CreatorAccountProps) => {
  const { profileUrl, followers, totalItems, itemsRetrieved, status } = account;
  const theme = useTheme();
  const platform = getPlatformFromUrl(profileUrl);
  const statusTooltip = STATUS_TOOLTIPS[status]?.(platform);

  const statusLabel = (
    <Typography variant="body2" fontWeight={500}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </Typography>
  );

  return (
    <>
      <Stack
        gap={2}
        sx={{ cursor: profileUrl ? 'pointer' : 'default' }}
        onClick={() => profileUrl && window.open(profileUrl, '_blank')}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Stack>
            <Link underline={profileUrl ? 'hover' : 'none'}>
              <Typography variant="body2" color="secondary">
                {normalizeTag(profileUrl)}
              </Typography>
            </Link>
            {account.restrictions.map((restriction, i) => (
              <Stack key={i} direction="row" alignItems="center" gap={0.5}>
                <Lock color="action" sx={{ fontSize: 14 }} />
                <Typography variant="caption" color="textSecondary">
                  {getRestrictionText(restriction)}
                </Typography>
              </Stack>
            ))}
          </Stack>
          <Stack
            direction="row"
            gap={1}
            justifyContent="center"
            alignItems="center"
          >
            <Typography variant="body2" flexDirection="row">
              Status:
            </Typography>
            {statusTooltip ? (
              <Tooltip placement="top" title={statusTooltip} arrow>
                {statusLabel}
              </Tooltip>
            ) : (
              statusLabel
            )}
          </Stack>
        </Stack>
        {status === 'active' && (
          <Stack
            direction="row"
            padding="12px 16px"
            gap={2}
            justifyContent="center"
            alignItems="center"
            flexWrap={{ xs: 'wrap', sm: 'nowrap' }}
          >
            <Stack gap={1} alignItems="flex-start" flexGrow={1}>
              <Typography variant="caption" noWrap>
                Followers
              </Typography>
              <Typography variant="subtitle1">
                {nFormatter(followers)}
              </Typography>
            </Stack>
            <Divider
              orientation="vertical"
              flexItem
              sx={{
                display: { xs: 'none', sm: 'flex' }
              }}
            />
            <Stack gap={1} alignItems="flex-start" flexGrow={1}>
              <Typography variant="caption" noWrap>
                Total Posts
              </Typography>
              <Typography variant="subtitle1">
                {nFormatter(totalItems)}
              </Typography>
            </Stack>
            <Divider
              orientation="vertical"
              flexItem
              sx={{
                display: { xs: 'none', sm: 'flex' }
              }}
            />
            <Stack gap={1} alignItems="flex-start" flexGrow={1}>
              <Typography variant="caption" noWrap>
                Posts Retrieved
              </Typography>
              <Typography variant="subtitle1">
                {nFormatter(itemsRetrieved)}
              </Typography>
            </Stack>
          </Stack>
        )}
        {status === 'pending' && <LinearProgress color="secondary" />}
      </Stack>
      {showBorderBottom && (
        <Divider flexItem color={theme.palette.secondary.main} />
      )}
    </>
  );
};

export default CreatorAccount;
